import React, { type FunctionComponent } from "react";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
  PaperClipOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import { Badge, Button, Popconfirm, Popover, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

import {
  AutomationMessageType,
  type ScheduledMessagesAutomation,
} from "@fitness-app/data-models/entities/AutomationTemplate";
import { ProgramAutomationTaskStatus } from "@fitness-app/data-models/entities/ProgramAutomation";

import { formatSentTime, formatSentTimeLong } from "~/modules/Automation/utils/formatSentTime";

interface OwnProps {
  item: ScheduledMessagesAutomation;
  onRemove?: () => void;
  onEdit?: () => void;
  status?: ProgramAutomationTaskStatus;
  sentDate?: string;
}

type Props = OwnProps;

const statusMapper = {
  [ProgramAutomationTaskStatus.Upcoming]: "processing",
  [ProgramAutomationTaskStatus.Done]: "success",
  [ProgramAutomationTaskStatus.Rejected]: "error",
  [ProgramAutomationTaskStatus.Canceled]: "warning",
  // Nie było czego zrobić i to normalne (np. brak bieżącej edycji wyzwania) —
  // szary, żeby nie mylił się z błędem ani z odfiltrowaniem klientki.
  [ProgramAutomationTaskStatus.Skipped]: "default",
} as const;

const MessageAutomationItem: FunctionComponent<Props> = ({ item, onRemove, status, onEdit, sentDate }) => {
  const { t } = useTranslation("automation");

  const handleEdit: React.MouseEventHandler<HTMLElement> = (event) => {
    event.stopPropagation();
    onEdit?.();
  };

  return (
    <Popover
      title={t("events.scheduleMessagesLong")}
      content={
        <Space direction="vertical" className="max-w-[400px]">
          {status && (
            <Space>
              <Typography.Text type="secondary">{t("events.status")}</Typography.Text>
              <Badge status={statusMapper[status] || "default"} text={t(`eventsStatus.${status || "upcoming"}`)} />
            </Space>
          )}
          <Space>
            <Typography.Text type="secondary">{t("events.sequenceName")}</Typography.Text>
            <Typography.Text>{item.name}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text type="secondary">{t("events.sentTime")}</Typography.Text>
            <Typography.Text>{formatSentTimeLong(item.sentTime)}</Typography.Text>
          </Space>
          {item.sentTime.mode === "relative" && item.sentTime.avoidSendingOnTheNight && (
            <Space>
              <Typography.Text type="secondary">Unikaj wysyłania w nocy:</Typography.Text>
              <Typography.Text>Tak (22:00-08:00)</Typography.Text>
            </Space>
          )}
          <Space>
            <Typography.Text type="secondary">Typ:</Typography.Text>
            <Typography.Text>
              {item.messageType === AutomationMessageType.Recurring ? "Cykliczny" : "Jednorazowy"}
            </Typography.Text>
          </Space>
          {item.filters && item.filters?.length > 0 ? (
            <Space direction="vertical">
              <Space>
                <Typography.Text type="secondary">Filtry:</Typography.Text>
                <Typography.Text>
                  {item.filters
                    .map((filter) => {
                      if (filter.type === "calendarEvents") {
                        return `${t(`dashboard:automatedMessageForm.${filter.condition}`)} (${t(`dashboard:automatedMessageForm.${filter.range}`)})`;
                      } else {
                        return `${t(`dashboard:automatedMessageForm.${filter.condition}`)} (${filter.tags.join(", ")})`;
                      }
                    })
                    .join(` ${item.filterOperator || "OR"} `)}
                </Typography.Text>
              </Space>
              {item.filters.length > 1 && item.filterOperator && (
                <Space>
                  <Typography.Text type="secondary">Operator:</Typography.Text>
                  <Typography.Text strong>
                    {item.filterOperator === "OR"
                      ? t("dashboard:automatedMessageForm.filterOperatorOr")
                      : t("dashboard:automatedMessageForm.filterOperatorAnd")}
                  </Typography.Text>
                </Space>
              )}
            </Space>
          ) : null}
          <Space>
            <Typography.Text type="secondary">{t("events.messagesCount")}</Typography.Text>
            <Typography.Text>{item.messages.length}</Typography.Text>
          </Space>
          {item.attachments?.length ? (
            <Space align="start">
              <Typography.Text type="secondary">
                <PaperClipOutlined /> Załączniki:
              </Typography.Text>
              <Typography.Text>{item.attachments.map((file) => file.shortName).join(", ")}</Typography.Text>
            </Space>
          ) : null}
          <Space>
            <Typography.Text type="secondary">{t("events.messagesAuthor")}</Typography.Text>
            <Typography.Text>{item.authorName}</Typography.Text>
          </Space>

          <Space direction="vertical">
            <Typography.Text type="secondary">Wiadomości:</Typography.Text>
            <ul>
              {item.messages.map((message, index) => (
                <li key={index}>
                  <Typography.Text>- {message}</Typography.Text>
                </li>
              ))}
            </ul>
          </Space>

          <Space>
            {onEdit && status !== ProgramAutomationTaskStatus.Done && (
              <Button icon={<EditOutlined style={{ pointerEvents: "none" }} />} type="link" onClick={handleEdit} />
            )}
            {onRemove && status !== ProgramAutomationTaskStatus.Done && (
              <Popconfirm
                title={t("automatedMessageForm.deleteEvent")}
                okText={t("common:switch.yes")}
                cancelText={t("common:button.cancel")}
                placement="top"
                onConfirm={onRemove}
              >
                <Button danger icon={<DeleteOutlined style={{ pointerEvents: "none" }} />} type="text" />
              </Popconfirm>
            )}
          </Space>
        </Space>
      }
    >
      <MessageOutlined className="mr-2" />
      <Typography.Text>{formatSentTime(item.sentTime, sentDate)}</Typography.Text>
      {status === ProgramAutomationTaskStatus.Done && <CheckOutlined className="ml-2 text-green-500" />}
      {status === ProgramAutomationTaskStatus.Canceled && <CloseOutlined className="ml-2 text-orange-500" />}
      {item.messageType === AutomationMessageType.Recurring && <RetweetOutlined className="ml-2 text-blue-500" />}
    </Popover>
  );
};

export default MessageAutomationItem;
